import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../Navbar/Navbar';
import './Servers.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import Modal from '../Modal/DeleteServer/DeleteServer';
import EditModal from '../Modal/EditModal/EditModal';
import { Link } from 'react-router-dom';
import ApiClient from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../ThemeContext';  // Import useTheme hook
import { useData } from '../../context/DataContext';


const Servers = () => {
  const { user } = useAuth();
  const {
    servers: cachedServers,
    environments: cachedPhases,
    refreshServers,
    refreshEnvironments,
  } = useData();
  const [servers, setServers] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [phases, setPhases] = useState([]);
  const [error, setError] = useState('');
  const [role, setRole] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletionResult, setDeletionResult] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedServer, setSelectedServer] = useState(null);

  const serversPerPage = 7;
  const { theme } = useTheme();  // Get the current theme


  useEffect(() => {
    const fetchRole = async () => {
      try {
        setRole(user?.role || 'normal');
      } catch (error) {
        console.error('Error fetching role:', error);
        setRole('normal');
        setError('Unable to load role. Using default access.');
        setTimeout(() => setError(''), 3000);
      }
    };

    fetchRole();
  }, [user]);

  const applyFilters = useCallback(() => {
    let filtered = servers;

    if (statusFilter) {
      filtered = filtered.filter((server) => server.status === statusFilter);
    }

    if (phaseFilter) {
      filtered = filtered.filter((server) => server.env && server.env._id === phaseFilter);
    }

    setFilteredServers(filtered);
  }, [servers, statusFilter, phaseFilter]);

  useEffect(() => {
    setServers(cachedServers || []);
    setPhases(cachedPhases || []);
  }, [cachedPhases, cachedServers]);

  useEffect(() => {
    const fetchLiveData = async () => {
      await refreshServers();
      await refreshEnvironments();
    };
    fetchLiveData();
    const interval = setInterval(() => {
      refreshServers();
    }, 20000);
    return () => clearInterval(interval);
  }, [refreshEnvironments, refreshServers]);

  useEffect(() => {
    applyFilters();
  }, [servers, statusFilter, phaseFilter, applyFilters]);

  const handleDeleteServer = async (serverId) => {
    setIsDeleting(true);
    try {
    const response = await ApiClient.delete(`/api/servers/${serverId}`, { params: { organization: user?.organization || 'default' } });
      if (response.status === 200) {
        setDeletionResult('Server deleted successfully');
        await refreshServers();
        await refreshEnvironments();
      } else {
        setDeletionResult('Failed to delete server.');
      }
    } catch (err) {
      console.error('Error deleting server:', err);
      setDeletionResult('Error deleting server.');
    } finally {
      setIsDeleting(false);
      setTimeout(() => {
        setDeletionResult('');
        closeDeleteModal();
      }, 3000);
    }
  };

  const handleEditServer = (server) => {
    setSelectedServer(server);
    setShowEditModal(true);
  };

  const openDeleteModal = (server) => {
    setSelectedServer(server);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletionResult('');
  };

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  const indexOfLastServer = currentPage * serversPerPage;
  const indexOfFirstServer = indexOfLastServer - serversPerPage;
  const currentServers = filteredServers.slice(indexOfFirstServer, indexOfLastServer);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPages = Math.ceil(filteredServers.length / serversPerPage);
  const totalServers = servers.length;
  const upCount = servers.filter(s => s.status === 'up').length;
  const downCount = servers.filter(s => s.status === 'down').length;

  return (
    <div className={`Servers-container ${theme}`}>
      <Navbar />
      <div className="Servers-content page-transition">
        <div className="page-shell server-dev-status-page">
          <div className="servers-top">
            <div>
              <h1 className="page-title">Servers</h1>
              <p className="servers-subtitle">Track uptime and environments for your fleet.</p>
            </div>
            <div className="server-stats">
              <span className="stat-chip">Total: {totalServers}</span>
              <span className="stat-chip up">Up: {upCount}</span>
              <span className="stat-chip down">Down: {downCount}</span>
            </div>
          </div>

          {error && <div className="notification error">{error}</div>}
          {deletionResult && <div className="notification success">{deletionResult}</div>}

          <div className="filters-actions-row">
            <div className="filter-container">
              <label>
                Status
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="up">Up</option>
                  <option value="down">Down</option>
                  <option value="pending">Pending</option>
                  <option value="unknown">Unknown</option>
                </select>
              </label>

              <label>
                Environment
                <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
                  <option value="">All</option>
                  {phases.map((phase) => (
                    <option key={phase._id} value={phase._id}>
                      {phase.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {role === 'admin' && (
              <div className="servers-actions inline">
                <Link to="/AddServer" className="addnew-button">+ Add Server</Link>
              </div>
            )}
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Server Name</th>
                  <th>Server Link</th>
                  <th>Current Environment</th>
                  <th className="status-col">Status</th>
                  {role === 'admin' && <th className="actions-col">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentServers.map((server) => (
                  <tr key={server._id}>
                    <td>{server.name}</td>
                    <td>
                      {server.link ? (
                        <a href={server.link} target="_blank" rel="noopener noreferrer" className="server-link">
                          {server.link}
                        </a>
                      ) : (
                        'No Link Available'
                      )}
                    </td>
                    <td>{server.env ? server.env.name : 'No Phase'}</td>
                    <td className="status-cell">
                      <span className={`status-badge ${server.status}`}>
                        {server.status === 'up'
                          ? '✔'
                          : server.status === 'down'
                          ? '✖'
                          : server.status === 'pending'
                          ? '…'
                          : '•'}
                      </span>
                    </td>
                    {role === 'admin' && (
                      <td className="actions-cell">
                        <button className="icon-button ghost" title="Edit" onClick={() => handleEditServer(server)}>
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button className="icon-button ghost danger" title="Delete" onClick={() => openDeleteModal(server)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    )}
                 </tr>
               ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index + 1}
                className={`page-button ${currentPage === index + 1 ? 'active' : ''}`}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {/* Delete Modal */}
          <Modal
            show={showDeleteModal}
            onClose={closeDeleteModal}
            onConfirm={() => handleDeleteServer(selectedServer._id)}
            server={selectedServer}
            isDeleting={isDeleting}
            deletionResult={deletionResult}
          />

          {/* Edit Modal */}
          {selectedServer && (
            <EditModal
              show={showEditModal}
              onClose={closeEditModal}
              server={selectedServer}
              onUpdateSuccess={refreshServers} // Refresh from shared cache after update
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Servers;
